import React, { useState } from 'react';
import { 
  Box, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Typography, 
  Avatar,
  Chip,
  IconButton,
  Collapse,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import { 
  Dashboard, 
  TrendingUp, 
  People, 
  Inventory, 
  ShoppingCart, 
  Assessment, 
  Settings,
  AttachMoney,
  Psychology,
  CompareArrows,
  Star,
  ChevronLeft,
  ChevronRight,
  Menu
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSidebar } from '../../context/SidebarContext';

/**
 * Sidebar Colapsable Premium - Inspirado en las referencias
 * Con funcionalidad de expandir/colapsar como los mejores dashboards
 */
export default function CollapsibleSidebar({ userData }) {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { expanded, toggleExpanded } = useSidebar();

  const menuItems = [
    {
      title: 'Dashboard',
      path: '/dashboard',
      icon: <Dashboard />,
      color: theme.palette.primary.main,
      description: 'Vista general'
    },
    {
      title: 'Productos',
      path: '/products',
      icon: <Inventory />,
      color: theme.palette.success.main,
      description: 'Catálogo y análisis',
      badge: 'Premium'
    },
    {
      title: 'Estadísticas',
      path: '/stats',
      icon: <TrendingUp />,
      color: theme.palette.info.main,
      description: 'Métricas avanzadas'
    },
    {
      title: 'Clientes',
      path: '/customers',
      icon: <People />,
      color: theme.palette.secondary.main,
      description: 'Segmentación RFM',
      badge: 'Nuevo'
    },
    {
      title: 'Ventas',
      path: '/sales',
      icon: <AttachMoney />,
      color: theme.palette.warning.main,
      description: 'Análisis de ventas'
    },
    {
      title: 'Carritos',
      path: '/carts',
      icon: <ShoppingCart />,
      color: theme.palette.error.main,
      description: 'Abandonos y recuperación'
    },
    {
      title: 'Predicciones',
      path: '/predictions',
      icon: <Psychology />,
      color: theme.palette.info.main,
      description: 'Insights predictivos',
      badge: 'IA'
    },
    {
      title: 'Competencia',
      path: '/competitive',
      icon: <CompareArrows />,
      color: theme.palette.warning.main,
      description: 'Próximamente',
      badge: 'Próximamente'
    },
    {
      title: 'Reportes',
      path: '/reports',
      icon: <Assessment />,
      color: theme.palette.primary.main,
      description: 'Informes detallados'
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
  };

  const isActive = (path) => location.pathname === path;



  return (
    <Box
      sx={{
        width: expanded ? 280 : 80,
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        background: `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        overflowX: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{ p: expanded ? 3 : 2, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.08)}` }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={expanded ? 2 : 1}>
          <Box display="flex" alignItems="center" gap={expanded ? 2 : 0}>
            <Avatar 
              sx={{ 
                width: 40, 
                height: 40,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                fontSize: '1.2rem',
                fontWeight: 700
              }}
            >
              N
            </Avatar>
            <Collapse in={expanded} orientation="horizontal">
              <Box>
                <Typography variant="h6" fontWeight={700} color="primary.main">
                  Nubelytics
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Analytics Premium
                </Typography>
              </Box>
            </Collapse>
          </Box>
          
          <IconButton 
            onClick={toggleExpanded}
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.2),
              }
            }}
          >
            {expanded ? <ChevronLeft /> : <ChevronRight />}
          </IconButton>
        </Box>
        
        <Collapse in={expanded}>
          {userData && (
            <Box 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                background: alpha(theme.palette.primary.main, 0.04),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
              }}
            >
              <Typography variant="body2" fontWeight={600} color="text.primary" noWrap>
                {userData.name || 'Usuario'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                Store ID: {userData.tiendaId || 'N/A'}
              </Typography>
            </Box>
          )}
        </Collapse>
      </Box>

      {/* Navigation */}
      <List sx={{ p: expanded ? 2 : 1, flex: 1, overflowY: 'auto' }}>
        {menuItems.map((item, index) => (
          <ListItem key={index} disablePadding sx={{ mb: 1 }}>
            <Tooltip title={!expanded ? item.title : ''} placement="right">
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                sx={{
                  borderRadius: 2,
                  minHeight: 48,
                  px: expanded ? 2 : 1.5,
                  background: isActive(item.path) 
                    ? `linear-gradient(135deg, ${alpha(item.color, 0.1)} 0%, ${alpha(item.color, 0.05)} 100%)`
                    : 'transparent',
                  border: isActive(item.path) 
                    ? `1px solid ${alpha(item.color, 0.2)}`
                    : '1px solid transparent',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${alpha(item.color, 0.08)} 0%, ${alpha(item.color, 0.04)} 100%)`,
                    border: `1px solid ${alpha(item.color, 0.16)}`,
                    transform: expanded ? 'translateX(4px)' : 'scale(1.05)',
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: expanded ? 48 : 'auto', justifyContent: 'center' }}>
                  <Avatar 
                    sx={{ 
                      width: 32, 
                      height: 32,
                      bgcolor: alpha(item.color, 0.1),
                      color: item.color,
                      border: `1px solid ${alpha(item.color, 0.2)}`
                    }}
                  >
                    {React.cloneElement(item.icon, { fontSize: 'small' })}
                  </Avatar>
                </ListItemIcon>
                
                <Collapse in={expanded} orientation="horizontal">
                  <ListItemText 
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight={isActive(item.path) ? 600 : 500}>
                          {item.title}
                        </Typography>
                        {item.badge && (
                          <Chip 
                            label={item.badge} 
                            size="small" 
                            sx={{ 
                              height: 18,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              bgcolor: alpha(item.color, 0.1),
                              color: item.color,
                              border: `1px solid ${alpha(item.color, 0.2)}`
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {item.description}
                      </Typography>
                    }
                  />
                </Collapse>
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>

      {/* Footer */}
      <Box sx={{ p: expanded ? 2 : 1, mt: 'auto', flexShrink: 0 }}>
        <Tooltip title={!expanded ? 'Configuración' : ''} placement="right">
          <ListItemButton
            onClick={() => handleNavigation('/settings')}
            sx={{
              borderRadius: 2,
              minHeight: 48,
              px: expanded ? 2 : 1.5,
              '&:hover': {
                background: alpha(theme.palette.action.hover, 0.04),
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: expanded ? 48 : 'auto', justifyContent: 'center' }}>
              <Settings color="action" />
            </ListItemIcon>
            <Collapse in={expanded} orientation="horizontal">
              <ListItemText 
                primary={
                  <Typography variant="body2" color="text.secondary">
                    Configuración
                  </Typography>
                }
              />
            </Collapse>
          </ListItemButton>
        </Tooltip>
        
        <Collapse in={expanded}>
          <Box 
            sx={{ 
              mt: 2,
              p: 2, 
              borderRadius: 2, 
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.1)} 100%)`,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              textAlign: 'center'
            }}
          >
            <Star sx={{ color: theme.palette.success.main, mb: 1 }} />
            <Typography variant="caption" color="text.secondary" display="block">
              Análisis Premium
            </Typography>
            <Typography variant="body2" fontWeight={600} color="success.main">
              Nivel Enterprise
            </Typography>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}
